<template>
  <div>
    <PForm
      title="Ajouter un nouveau badge"
      update-title="Modifier un badge"
      activator-label="Ajouter un nouveau badge"
      size="lg"
      :submit-fn="submit"
      :updating="updating"
      @update:updating="$emit('update:updating', $event)"
      @open="onOpen"
      @reset="reset"
    >
      <template #activator="{ open, label }">
        <PButton v-if="authStore.ability.can('create', 'LetterFileTag')" size="lg" @click="open">{{ label }}</PButton>
        <!-- btn won't hide otherwise because it fallbacks to slot's default content -->
        <div v-else></div>
      </template>
      <div class="flex flex-col gap-2">
        <PInput label="Nom" v-model="name" :error-messages="nameErrors" class="grow" />

        <PTooltip
          v-if="isMaxTagsDefault"
          :offset="5"
          :delay="500"
          :label="`Le maximum de ${userPreferencesStore?.maxActivatedTags} badge par défaut est atteint`"
        >
          <PCheckbox disabled label="Par défaut" v-model="isDefault" hide-messages class="grow" />
        </PTooltip>
        <PCheckbox v-else label="Par défaut" v-model="isDefault" hide-messages class="grow" />

        <PCheckbox label="Afficher dans les mails" v-model="showInEmail" hide-messages class="grow" />
      </div>
    </PForm>
  </div>
</template>

<script setup lang="ts">
import {
  PForm,
  PButton,
  PInput,
  usePastaValidation,
  pickModified,
  PCheckbox,
  PTooltip,
  type Nullable,
} from "@pasta/box-v2";
import { required } from "@vuelidate/validators";
import { useAuthStore } from "@pasta/front-auth-v2";
import { type LetterFileTagDto } from "../app/dto/letter-file-tag.dto";
import { useAdminTagsApi } from "./admin-tags.api";
import { ref } from "vue";
import { useUserPreferencesStore } from "../app/stores/user-preferences.store";

interface Props {
  updating?: Nullable<LetterFileTagDto>;
}
const props = withDefaults(defineProps<Props>(), { updating: null });
const emit = defineEmits<{
  (e: "refresh"): void;
  (e: "update:updating", value: Nullable<LetterFileTagDto>): void;
}>();

const authStore = useAuthStore();

const tagsApi = useAdminTagsApi();
const $validation = usePastaValidation();
const userPreferencesStore = useUserPreferencesStore();

const [name, nameErrors] = $validation.ref<string>("", [{ validator: required, message: "Ce champ est requis" }]);

const isDefault = ref<boolean>(false);
const showInEmail = ref<boolean>(false);
const isMaxTagsDefault = ref<boolean>(false);

async function submit() {
  const isValid = await $validation.validate();
  if (!isValid) return false;

  if (!props.updating) {
    if (!confirm("Attention vous ne pourrez pas supprimer le badge, êtes vous sûr(e) de vouloir ajouter ce badge ?")) {
      return false;
    }

    await tagsApi.create({
      name: name.value.trim(),
      showInEmail: showInEmail.value,
      default: isDefault.value,
    });
  } else {
    await tagsApi.update(
      props.updating.id,
      pickModified(
        {
          name: name.value.trim(),
          showInEmail: showInEmail.value,
          default: isDefault.value,
        },
        props.updating
      )
    );
  }

  emit("refresh");
  return true;
}

async function onOpen() {
  if (props.updating) {
    name.value = props.updating.name;
    showInEmail.value = props.updating.showInEmail ?? false;
    isDefault.value = props.updating.default ?? false;
  }

  const tags = await tagsApi.fetchList({ offset: 0, limit: 1000 });
  isMaxTagsDefault.value =
    tags.data.filter(t => t.default).length >= userPreferencesStore.maxActivatedTags && !isDefault.value;
}

async function reset() {
  name.value = "";
  showInEmail.value = false;
  isDefault.value = false;
  $validation.reset();
}
</script>
